/**
 * Order alert sound controls — the mute button and the sound menu (Sound 1 /
 * Sound 2 / No Sound, volume, Test Sound) for one order channel.
 *
 * The Orders page sets the Deeliver app order sound; the Uber Orders page sets
 * the Uber Eats order sound. Playback itself happens globally in
 * OrderNotificationProvider, so alerts sound on every page.
 */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Slider,
  Stack,
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import CheckIcon from '@mui/icons-material/Check';
import { useHUDTheme } from './OrdersHUD/theme';
import { useOrderSound } from '../hooks/useOrderSound';
import type { OrderChannel, SoundOption } from '../hooks/useOrderSound';

const SOUND_OPTIONS: { value: SoundOption; label: string }[] = [
  { value: 'notification-1', label: 'Sound 1' },
  { value: 'notification-2', label: 'Sound 2' },
  { value: 'none', label: 'No Sound' },
];

const CHANNEL_LABEL: Record<OrderChannel, string> = {
  deeliver: 'Deeliver app orders',
  uber: 'Uber Eats orders',
};

export default function OrderSoundControls({ channel }: { channel: OrderChannel }) {
  const { hud } = useHUDTheme();
  const { sound, muted, volume, playSound, setSound, toggleMute, setVolume } = useOrderSound(channel);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const label = CHANNEL_LABEL[channel];

  const handleClose = () => setAnchorEl(null);
  const handleSoundSelect = (selectedSound: SoundOption) => {
    setSound(selectedSound);
    handleClose();
  };

  return (
    <>
      <Tooltip title={muted ? `Unmute ${label}` : `Mute ${label}`}>
        <IconButton
          onClick={toggleMute}
          size="small"
          sx={{ color: muted ? hud.textMuted : hud.accent, '&:hover': { bgcolor: hud.hover } }}
        >
          {muted ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title={`Sound settings — ${label}`}>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          sx={{ color: hud.textSecondary, '&:hover': { bgcolor: hud.hover } }}
        >
          <MusicNoteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: hud.elevated,
              border: `1px solid ${hud.border}`,
              '& .MuiMenuItem-root': {
                color: hud.textPrimary,
                '&:hover': { bgcolor: hud.hover },
                '&.Mui-selected': { bgcolor: hud.surface },
              },
            },
          },
        }}
      >
        <Typography variant="caption" sx={{ color: hud.textSecondary, display: 'block', px: 2, pt: 0.5, pb: 1 }}>
          New order sound — {label}
        </Typography>
        {SOUND_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleSoundSelect(option.value)}
            selected={sound === option.value}
          >
            <ListItemIcon sx={{ color: hud.accent }}>
              {sound === option.value ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
        <Divider sx={{ borderColor: hud.border }} />
        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography variant="caption" sx={{ color: hud.textSecondary, display: 'block', mb: 1 }}>
            Volume
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <VolumeOffIcon fontSize="small" sx={{ color: hud.textMuted }} />
            <Slider
              value={volume}
              onChange={(_, value) => setVolume(value as number)}
              size="small"
              min={0}
              max={100}
              disabled={sound === 'none'}
              sx={{
                flex: 1,
                color: hud.accent,
                '& .MuiSlider-thumb': { bgcolor: hud.accent },
                '& .MuiSlider-track': { bgcolor: hud.accent },
                '& .MuiSlider-rail': { bgcolor: hud.border },
              }}
            />
            <VolumeUpIcon fontSize="small" sx={{ color: hud.textMuted }} />
          </Stack>
        </Box>
        <Divider sx={{ borderColor: hud.border }} />
        <MenuItem
          onClick={() => {
            playSound();
            handleClose();
          }}
          disabled={sound === 'none' || muted}
        >
          <ListItemText>Test Sound</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
